import { useEffect, useState } from 'react';
import { callApi } from '../api';
import { Section, ErrorBox, LoadingSpinner, EmptyState } from '../components/Section';
import { UserChip } from '../components/UserChip';
import { WindowChips, windowLabel } from '../components/WindowChips';
import { useSession } from '../hooks/useSession';
import { timeAgo } from '../utils';

const SOURCES = ['all', 'beli', 'google'];
const UNMATCHED_WINDOWS = [
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 365, label: '1y' },
  { value: 3650, label: 'all' },
];

export function UnmatchedObservations() {
  const { session } = useSession();
  const [source, setSource] = useState('all');
  const [days, setDays] = useState(90);
  const [cities, setCities] = useState(null);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const refresh = async (src = source, d = days) => {
    setLoading(true);
    setError('');
    try {
      const [c, list] = await Promise.all([
        callApi(`/api/v1/admin/imports/unmatched/cities?source=${src}&days=${d}`, {
          accessToken: session.access_token,
        }),
        callApi(`/api/v1/admin/imports/unmatched?source=${src}&limit=50`, {
          accessToken: session.access_token,
        }),
      ]);
      setCities(c);
      setItems(list.items);
      setHasMore(list.has_more);
      setCursor(list.next_cursor);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(source, days); }, [source, days, session.access_token]);

  const loadMore = async () => {
    try {
      const d = await callApi(
        `/api/v1/admin/imports/unmatched?source=${source}&limit=50&cursor=${encodeURIComponent(cursor || '')}`,
        { accessToken: session.access_token }
      );
      setItems((p) => [...p, ...d.items]);
      setHasMore(d.has_more);
      setCursor(d.next_cursor);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-stone-800">Unmatched observations</h1>
        <div className="flex gap-1">
          {SOURCES.map((s) => (
            <button
              key={s}
              onClick={() => setSource(s)}
              className={`text-xs px-3 py-1 rounded capitalize ${
                source === s
                  ? 'bg-stone-800 text-white'
                  : 'bg-white text-stone-700 border'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <ErrorBox message={error} />

      <Section
        title={`Top non-NYC cities (${windowLabel(days, UNMATCHED_WINDOWS)})`}
        action={
          <WindowChips
            value={days}
            onChange={setDays}
            options={UNMATCHED_WINDOWS}
          />
        }
      >
        {loading || !cities ? <LoadingSpinner /> : (
          cities.rows.length === 0 ? <EmptyState>No non-NYC observations yet.</EmptyState> : (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-xs text-stone-500 border-b pb-1">
                <span>City</span>
                <span>imports / unique users</span>
              </div>
              {cities.rows.map((r, i) => (
                <div key={i} className="flex justify-between">
                  <span>
                    {r.city || '—'}
                    {r.country && <span className="text-stone-500">, {r.country}</span>}
                  </span>
                  <span className="text-stone-700">
                    {r.n} / {r.unique_users}
                  </span>
                </div>
              ))}
            </div>
          )
        )}
      </Section>

      <Section title="Recent observations">
        {loading ? <LoadingSpinner /> : (
          items.length === 0 ? <EmptyState>Nothing here.</EmptyState> : (
            <div className="divide-y">
              {items.map((o) => (
                <div key={o.id} className="py-2 text-sm">
                  <div className="font-medium">
                    {o.name}
                    <span className="ml-2 text-xs bg-stone-100 rounded px-1.5">
                      {o.source}
                    </span>
                  </div>
                  <div className="text-xs text-stone-500">
                    {[o.neighborhood, o.city, o.country].filter(Boolean).join(' · ')}
                    {o.import_context && ` · ${o.import_context}`}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-2">
                    <UserChip userId={o.palate_user_id} displayName={o.user_display_name} />
                    <span>{o.unmatched_reason} · {timeAgo(o.imported_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
        {hasMore && (
          <button
            onClick={loadMore}
            className="text-sm text-stone-700 underline mt-2"
          >
            Load more
          </button>
        )}
      </Section>
    </div>
  );
}
